import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Checkbox } from '../../../components/ui/checkbox';
import { Input } from '../../../components/ui/input';
import {
  Shield, Save, RotateCcw, Users, Search, ChevronRight,
  History, AlertTriangle, CheckCircle2,
} from 'lucide-react';

const BACKEND_URL = (() => {
  const env = process.env.REACT_APP_BACKEND_URL;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  if (origin && !origin.includes('preview.emergentagent.com') && !origin.includes('localhost')) return origin;
  return env || origin;
})();

const api = axios.create({ baseURL: `${BACKEND_URL}/api/admin/rbac` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const ROLE_COLORS = {
  super_admin:     'bg-red-50 text-red-700 border-red-200',
  operation_admin: 'bg-orange-50 text-orange-700 border-orange-200',
  pic:             'bg-blue-50 text-blue-700 border-blue-200',
  account:         'bg-emerald-50 text-emerald-700 border-emerald-200',
  agent:           'bg-violet-50 text-violet-700 border-violet-200',
  customer:        'bg-slate-50 text-slate-700 border-slate-200',
};

const DOMAIN_ICONS = {
  bookings: '🧾', properties: '🏢', units: '🚪', users: '👥', kyc: '🪪',
  payments: '💳', commissions: '💼', refunds: '↩️', loans: '🏦',
  leads: '🎯', appointments: '📅', analytics: '📊', rbac: '🛡️',
  settings: '⚙️', notifications: '🔔',
};

export default function RBAC() {
  const [me, setMe] = useState(null);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleDetail, setRoleDetail] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [granted, setGranted] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('matrix');

  useEffect(() => {
    (async () => {
      try {
        const [meR, rolesR] = await Promise.all([api.get('/me'), api.get('/roles')]);
        setMe(meR.data);
        setRoles(rolesR.data.roles || []);
        if ((rolesR.data.roles || []).length) setSelectedRole(rolesR.data.roles[0].code);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load RBAC data');
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedRole) return;
    (async () => {
      try {
        const r = await api.get(`/roles/${selectedRole}/permissions`);
        setRoleDetail(r.data);
        setGranted(new Set(r.data.granted || []));
        setDirty(false);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load role permissions');
      }
    })();
  }, [selectedRole]);

  const loadAudit = async () => {
    setAuditLoading(true);
    try {
      const r = await api.get('/audit-log?limit=100');
      setAudit(r.data.items || []);
    } catch (e) {
      toast.error('Failed to load audit log');
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => { if (tab === 'audit') loadAudit(); }, [tab]);

  const togglePerm = (code) => {
    if (selectedRole === 'super_admin') {
      toast.info('Super Admin keeps all permissions by design.');
      return;
    }
    const s = new Set(granted);
    if (s.has(code)) s.delete(code); else s.add(code);
    setGranted(s);
    setDirty(true);
  };

  const toggleDomain = (domain, checked) => {
    if (selectedRole === 'super_admin') return;
    const s = new Set(granted);
    (roleDetail?.domains || []).find((d) => d.domain === domain)?.permissions.forEach((p) => {
      if (checked) s.add(p.code); else s.delete(p.code);
    });
    setGranted(s);
    setDirty(true);
  };

  const save = async () => {
    if (!selectedRole || !dirty) return;
    setSaving(true);
    try {
      await api.put(`/roles/${selectedRole}/permissions`, { granted: Array.from(granted) });
      toast.success(`Permissions saved for ${selectedRole}`);
      setDirty(false);
      const r = await api.get(`/roles/${selectedRole}/permissions`);
      setRoleDetail(r.data);
      const rolesR = await api.get('/roles');
      setRoles(rolesR.data.roles || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!selectedRole) return;
    if (!window.confirm(`Reset ${selectedRole} permissions to factory defaults?`)) return;
    setSaving(true);
    try {
      await api.post(`/roles/${selectedRole}/reset`);
      toast.success(`Reset ${selectedRole} to defaults`);
      const r = await api.get(`/roles/${selectedRole}/permissions`);
      setRoleDetail(r.data);
      setGranted(new Set(r.data.granted || []));
      setDirty(false);
      const rolesR = await api.get('/roles');
      setRoles(rolesR.data.roles || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setSaving(false);
    }
  };

  const isSuperAdmin = me?.is_super_admin;
  const filteredDomains = useMemo(() => {
    if (!roleDetail) return [];
    if (!search) return roleDetail.domains;
    const q = search.toLowerCase();
    return roleDetail.domains
      .map((d) => ({
        ...d,
        permissions: d.permissions.filter(
          (p) => p.code.includes(q) || (p.label || '').toLowerCase().includes(q),
        ),
      }))
      .filter((d) => d.permissions.length > 0);
  }, [roleDetail, search]);

  if (!me) return <div className="p-6">Loading…</div>;

  if (!me.permissions?.includes('rbac.view')) {
    return (
      <div className="p-6 max-w-2xl mx-auto" data-testid="rbac-forbidden">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Access denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            You don't have permission to view the RBAC management page. Your role is <b>{me.role}</b>.
            Contact a Super Admin if you need access.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto" data-testid="rbac-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold flex items-center gap-2">
            <Shield className="w-7 h-7 text-[#4E50FF]" />
            Role-Based Access Control
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage permissions for built-in roles. Changes apply immediately to all users in that role.
          </p>
        </div>
        <Badge variant="outline" className="text-xs">
          Signed in as <b className="ml-1 capitalize">{me.role.replace('_', ' ')}</b>
        </Badge>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="matrix" data-testid="rbac-tab-matrix">Roles & Permissions</TabsTrigger>
          <TabsTrigger value="audit" data-testid="rbac-tab-audit">Audit Log</TabsTrigger>
        </TabsList>

        {/* MATRIX TAB */}
        <TabsContent value="matrix">
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
            {/* Role list */}
            <div className="space-y-2" data-testid="rbac-role-list">
              {roles.map((r) => (
                <button
                  key={r.code}
                  onClick={() => setSelectedRole(r.code)}
                  className={`w-full text-left p-3 rounded-xl border transition-all ${
                    selectedRole === r.code
                      ? 'border-[#4E50FF] ring-2 ring-[#4E50FF]/20 bg-[#4E50FF]/5'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                  data-testid={`rbac-role-${r.code}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`px-2 py-0.5 rounded-md text-xs font-medium border ${ROLE_COLORS[r.code] || ''}`}>
                      {r.label}
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                  <div className="text-xs text-gray-500 mt-2">{r.description}</div>
                  <div className="flex items-center gap-3 text-xs text-gray-600 mt-2">
                    <span>🔑 {r.granted_count}/{r.total_permissions}</span>
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" />{r.user_count}</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Permission editor */}
            {roleDetail && (
              <Card data-testid="rbac-permission-editor">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <CardTitle className="capitalize">{selectedRole.replace('_', ' ')} permissions</CardTitle>
                      <p className="text-xs text-gray-500 mt-1">
                        {granted.size} of {roleDetail.domains.reduce((a, d) => a + d.permissions.length, 0)} granted
                        {dirty && <span className="ml-2 text-orange-600 font-medium">• Unsaved changes</span>}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
                        <Input
                          placeholder="Filter permissions…"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="pl-8 w-56"
                          data-testid="rbac-perm-search"
                        />
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={reset}
                        disabled={!isSuperAdmin || saving}
                        title={!isSuperAdmin ? 'Super Admin only' : 'Reset to factory defaults'}
                        data-testid="rbac-reset-btn"
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />Reset
                      </Button>
                      <Button
                        size="sm"
                        onClick={save}
                        disabled={!isSuperAdmin || !dirty || saving}
                        className="bg-[#4E50FF]"
                        data-testid="rbac-save-btn"
                      >
                        <Save className="w-4 h-4 mr-1" />
                        {saving ? 'Saving…' : 'Save'}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {selectedRole === 'super_admin' && (
                    <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>Super Admin always has every permission. Editing is disabled by design — this safety rail prevents accidental lockout.</span>
                    </div>
                  )}
                  <div className="space-y-4">
                    {filteredDomains.map((d) => {
                      const all = d.permissions.every((p) => granted.has(p.code));
                      const some = !all && d.permissions.some((p) => granted.has(p.code));
                      return (
                        <div key={d.domain} className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`rbac-domain-${d.domain}`}>
                          <div className="flex items-center justify-between p-3 bg-gray-50 border-b border-gray-200">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{DOMAIN_ICONS[d.domain] || '📌'}</span>
                              <span className="font-semibold capitalize">{d.domain}</span>
                              <span className="text-xs text-gray-500">
                                ({d.permissions.filter((p) => granted.has(p.code)).length}/{d.permissions.length})
                              </span>
                            </div>
                            <label className="flex items-center gap-2 text-xs cursor-pointer">
                              <Checkbox
                                checked={all}
                                onCheckedChange={(v) => toggleDomain(d.domain, !!v)}
                                disabled={!isSuperAdmin || selectedRole === 'super_admin'}
                                data-testid={`rbac-domain-toggle-${d.domain}`}
                              />
                              <span>{all ? 'All' : some ? 'Some' : 'None'}</span>
                            </label>
                          </div>
                          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {d.permissions.map((p) => {
                              const checked = granted.has(p.code);
                              return (
                                <label
                                  key={p.code}
                                  className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                                    checked ? 'bg-[#4E50FF]/5 border border-[#4E50FF]/20' : 'border border-transparent hover:bg-gray-50'
                                  }`}
                                  data-testid={`rbac-perm-${p.code}`}
                                >
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={() => togglePerm(p.code)}
                                    disabled={!isSuperAdmin || selectedRole === 'super_admin'}
                                    className="mt-0.5"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium leading-tight">{p.label}</div>
                                    <div className="text-[11px] text-gray-500 font-mono">{p.code}</div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    {filteredDomains.length === 0 && (
                      <div className="text-center py-8 text-gray-500 text-sm">No permissions match "{search}"</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* AUDIT TAB */}
        <TabsContent value="audit">
          <Card data-testid="rbac-audit-log">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" /> Audit Log
              </CardTitle>
            </CardHeader>
            <CardContent>
              {auditLoading ? (
                <div className="text-center py-8 text-gray-500">Loading…</div>
              ) : audit.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No changes yet.</div>
              ) : (
                <div className="space-y-2">
                  {audit.map((row, i) => (
                    <div key={i} className="p-3 border border-gray-200 rounded-lg bg-white" data-testid={`rbac-audit-row-${i}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          <span className="font-medium text-sm">{row.action}</span>
                          <Badge variant="outline" className="text-[10px]">{row.target_type}</Badge>
                          <span className="text-xs font-mono text-gray-600">{row.target_id}</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {row.actor_email || row.actor_id} · {new Date(row.at).toLocaleString()}
                        </div>
                      </div>
                      {(row.after?.added?.length > 0 || row.after?.removed?.length > 0) && (
                        <div className="mt-2 ml-6 text-xs flex flex-wrap gap-3">
                          {row.after?.added?.length > 0 && (
                            <div className="text-emerald-700">
                              + {row.after.added.join(', ')}
                            </div>
                          )}
                          {row.after?.removed?.length > 0 && (
                            <div className="text-red-700">
                              − {row.after.removed.join(', ')}
                            </div>
                          )}
                        </div>
                      )}
                      {row.notes && (
                        <div className="mt-1 ml-6 text-xs text-gray-600 italic">"{row.notes}"</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
